import React, { useState, useEffect } from 'react';
import { Form, Button, Offcanvas } from 'react-bootstrap';
import { fetchCategories } from '../../Services/CategoryServices';
import './FilterSidebar.css';
import PropTypes from 'prop-types';

const FilterSidebar = ({ onFilterChange, initialFilters }) => {
  // State Management
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [priceRange, setPriceRange] = useState([
    initialFilters.minPrice !== null ? initialFilters.minPrice : '',
    initialFilters.maxPrice !== null ? initialFilters.maxPrice : '',
  ]);
  const [tempPriceRange, setTempPriceRange] = useState([
    initialFilters.minPrice !== null ? initialFilters.minPrice : '',
    initialFilters.maxPrice !== null ? initialFilters.maxPrice : '',
  ]);
  const [category, setCategory] = useState(initialFilters.category || null);
  const [size, setSize] = useState(initialFilters.size || null);
  const [availability, setAvailability] = useState(initialFilters.availability || null);
  const [categories, setCategories] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    categories: true,
    price: false,
    size: false,
    availability: false
  });

  // Debug: Log initialFilters on mount
  useEffect(() => {
    console.log('FilterSidebar Mounted with Initial Filters:', initialFilters);
  }, [initialFilters]);

  // Fetch categories when component mounts
  useEffect(() => {
    const getCategories = async () => {
      try {
        const result = await fetchCategories();
        console.log('Fetched Categories:', result);
        if (Array.isArray(result)) {
          const activeCategories = result.filter(
            (categoryItem) => categoryItem.Status === '1'
          );
          setCategories(activeCategories);
        } else if (result && result.data) {
          const activeCategories = result.data.filter(
            (categoryItem) => categoryItem.Status === '1'
          );
          setCategories(activeCategories);
        } else {
          console.error('No data found in result:', result);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    getCategories();
  }, []);

  // Sync with initialFilters changes
  useEffect(() => {
    console.log('FilterSidebar received new initialFilters:', initialFilters);
    setPriceRange([
      initialFilters.minPrice !== null ? initialFilters.minPrice : '',
      initialFilters.maxPrice !== null ? initialFilters.maxPrice : '',
    ]);
    setTempPriceRange([
      initialFilters.minPrice !== null ? initialFilters.minPrice : '',
      initialFilters.maxPrice !== null ? initialFilters.maxPrice : '',
    ]);
    setCategory(initialFilters.category || null);
    setSize(initialFilters.size || null);
    setAvailability(initialFilters.availability || null);
  }, [initialFilters]);

  // Handler functions
  const handleMinChange = (e) => {
    const value = e.target.value;
    const newMin = value === '' ? '' : parseInt(value, 10);
    setTempPriceRange([newMin, tempPriceRange[1]]);
  };

  const handleMaxChange = (e) => {
    const value = e.target.value;
    const newMax = value === '' ? '' : parseInt(value, 10);
    setTempPriceRange([tempPriceRange[0], newMax]);
  };

  const updateFilters = (updatedFilter) => {
    const newFilters = {
      category,
      size,
      availability,
      minPrice: priceRange[0],
      maxPrice: priceRange[1],
      ...updatedFilter,
    };
    console.log('FilterSidebar updating filters:', newFilters);
    onFilterChange(newFilters);
  };

  const handleCategoryChange = (selectedCategoryId) => {
    setCategory(selectedCategoryId);
    updateFilters({ category: selectedCategoryId });
  };

  const handleSizeChange = (selectedSize) => {
    setSize(selectedSize);
    updateFilters({ size: selectedSize });
  };

  const handleAvailabilityChange = (selectedAvailability) => {
    setAvailability(selectedAvailability);
    updateFilters({ availability: selectedAvailability });
  };

  const handleApplyPriceFilter = () => {
    const min = tempPriceRange[0] !== '' ? tempPriceRange[0] : null;
    const max = tempPriceRange[1] !== '' ? tempPriceRange[1] : null;
    setPriceRange([min, max]); // Update the actual priceRange
    updateFilters({ minPrice: min, maxPrice: max });
  };

  const renderPriceInputs = (isMobile = false) => (
    <div className={isMobile ? "mobile-filter-price-inputs" : "price-range-buttons"}>
      <input
        type="number"
        value={tempPriceRange[0] !== '' ? tempPriceRange[0] : ''}
        onChange={handleMinChange}
        className={isMobile ? "mobile-filter-price-input" : "price-input"}
        placeholder="Min"
        aria-label="Minimum Price"
        min="0"
      />
      <input
        type="number"
        value={tempPriceRange[1] !== '' ? tempPriceRange[1] : ''}
        onChange={handleMaxChange}
        className={isMobile ? "mobile-filter-price-input" : "price-input"}
        placeholder="Max"
        aria-label="Maximum Price"
        min="0"
      />
      <Button
        variant="dark"
        size={isMobile ? undefined : "sm"}
        onClick={handleApplyPriceFilter}
        className={isMobile ? "mobile-filter-price-apply" : "apply-button"}
      >
        Apply
      </Button>
    </div>
  );

  return (
    <div className="filter">
      {/* Desktop Sidebar */}
      <div className="filter-sidebar d-none d-xl-block">
        <h5 className="text-left">Categories</h5>
        <Form>
          <div className="ml-3 Categories-section">
            {categories.length > 0 ? (
              categories.map((categoryItem) => (
                <Form.Check
                  key={categoryItem.CategoryId}
                  type="radio"
                  name="category"
                  id={`category-${categoryItem.CategoryId}`}
                  label={categoryItem.Category}
                  className="my-2"
                  onChange={() => handleCategoryChange(categoryItem.CategoryId)}
                  checked={category === categoryItem.CategoryId}
                />
              ))
            ) : (
              <p>No categories available.</p>
            )}
            <Form.Check
              type="radio"
              name="category"
              id="categoryNone"
              label="None"
              className="my-2"
              onChange={() => handleCategoryChange(null)}
              checked={category === null}
            />
          </div>

          <h5 className="text-left">Price Range</h5>
          {renderPriceInputs()}

          <h5 className="text-left">Size</h5>
          <div className="ml-3 Size-section">
            {['XS', 'S', 'M'].map((sizeOption) => (
              <Form.Check
                key={sizeOption}
                type="radio"
                name="size"
                id={`size-${sizeOption}`}
                label={sizeOption}
                className="my-2"
                onChange={() => handleSizeChange(sizeOption)}
                checked={size === sizeOption}
              />
            ))}
            <Form.Check
              type="radio"
              name="size"
              id="sizeNone"
              label="None"
              className="my-2"
              onChange={() => handleSizeChange(null)}
              checked={size === null}
            />
          </div>

          <h5 className="text-left">Availability</h5>
          <div className="ml-3 availability-section">
            <Form.Check
              type="radio"
              name="availability"
              id="availabilityInStock"
              label="In Stock"
              className="my-2"
              onChange={() => handleAvailabilityChange('In Stock')}
              checked={availability === 'In Stock'}
            />
            <Form.Check
              type="radio"
              name="availability"
              id="availabilityOutOfStock"
              label="Out of Stock"
              className="my-2"
              onChange={() => handleAvailabilityChange('Out of Stock')}
              checked={availability === 'Out of Stock'}
            />
            <Form.Check
              type="radio"
              name="availability"
              id="availabilityNone"
              label="None"
              className="my-2"
              onChange={() => handleAvailabilityChange(null)}
              checked={availability === null}
            />
          </div>
        </Form>
      </div>

      {/* Mobile Filter Button and Offcanvas */}
      <Button 
        variant="outline-dark" 
        className="mobile-filter-trigger d-xl-none mb-4"
        onClick={() => setShowOffcanvas(true)}
      >
        <i className="fa-solid fa-sliders"></i>
        <span className="ms-2">Filter By</span>
      </Button>

      <Offcanvas 
        show={showOffcanvas} 
        onHide={() => setShowOffcanvas(false)} 
        placement="start" 
        className="mobile-filter-offcanvas d-xl-none"
      >
        <div className="mobile-filter-wrapper">
          <div className="mobile-filter-header">
            <div className="mobile-filter-header-content">
              <h2 className="mobile-filter-title">Filters</h2>
              <Button 
                variant="link" 
                className="mobile-filter-close-btn"
                onClick={() => setShowOffcanvas(false)}
              >
                <i className="fa-solid fa-x"></i>
              </Button>
            </div>
          </div>

          <div className="mobile-filter-body">
            {/* Categories Section */}
            <div className="mobile-filter-section">
              <button
                className="mobile-filter-section-header"
                onClick={() => setExpandedSections(prev => ({ ...prev, categories: !prev.categories }))}
              >
                <span className="mobile-filter-section-title">Categories</span>
                <span className="mobile-filter-icon">
                  {expandedSections.categories ? '−' : '+'}
                </span>
              </button>
              
              {expandedSections.categories && (
                <div className="mobile-filter-content">
                  {categories.map((categoryItem) => (
                    <div key={categoryItem.CategoryId} className="mobile-filter-option">
                      <label className="mobile-filter-label">
                        <input
                          type="radio"
                          name="category"
                          checked={category === categoryItem.CategoryId}
                          onChange={() => handleCategoryChange(categoryItem.CategoryId)}
                          className="mobile-filter-radio"
                        />
                        <span className="mobile-filter-label-text">{categoryItem.Category}</span>
                      </label>
                    </div>
                  ))}
                  <div className="mobile-filter-option">
                    <label className="mobile-filter-label">
                      <input
                        type="radio"
                        name="category"
                        checked={category === null}
                        onChange={() => handleCategoryChange(null)}
                        className="mobile-filter-radio"
                      />
                      <span className="mobile-filter-label-text">None</span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Price Range Section */}
            <div className="mobile-filter-section">
              <button
                className="mobile-filter-section-header"
                onClick={() => setExpandedSections(prev => ({ ...prev, price: !prev.price }))}
              >
                <span className="mobile-filter-section-title">Price Range</span>
                <span className="mobile-filter-icon">
                  {expandedSections.price ? '−' : '+'}
                </span>
              </button>
              
              {expandedSections.price && (
                <div className="mobile-filter-content">
                  {renderPriceInputs(true)}
                </div>
              )}
            </div>

            {/* Size Section */}
            <div className="mobile-filter-section">
              <button
                className="mobile-filter-section-header"
                onClick={() => setExpandedSections(prev => ({ ...prev, size: !prev.size }))}
              >
                <span className="mobile-filter-section-title">Size</span>
                <span className="mobile-filter-icon">
                  {expandedSections.size ? '−' : '+'}
                </span>
              </button>
              
              {expandedSections.size && (
                <div className="mobile-filter-content">
                  {['XS', 'S', 'M'].map((sizeOption) => (
                    <div key={sizeOption} className="mobile-filter-option">
                      <label className="mobile-filter-label">
                        <input
                          type="radio"
                          name="size"
                          checked={size === sizeOption}
                          onChange={() => handleSizeChange(sizeOption)}
                          className="mobile-filter-radio"
                        />
                        <span className="mobile-filter-label-text">{sizeOption}</span>
                      </label>
                    </div>
                  ))}
                  <div className="mobile-filter-option">
                    <label className="mobile-filter-label">
                      <input
                        type="radio"
                        name="size"
                        checked={size === null}
                        onChange={() => handleSizeChange(null)}
                        className="mobile-filter-radio"
                      />
                      <span className="mobile-filter-label-text">None</span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Availability Section */}
            <div className="mobile-filter-section">
              <button
                className="mobile-filter-section-header"
                onClick={() => setExpandedSections(prev => ({ ...prev, availability: !prev.availability }))}
              >
                <span className="mobile-filter-section-title">Availability</span>
                <span className="mobile-filter-icon">
                  {expandedSections.availability ? '−' : '+'}
                </span>
              </button>
              
              {expandedSections.availability && (
                <div className="mobile-filter-content">
                  {['In Stock', 'Out of Stock'].map((availabilityOption) => (
                    <div key={availabilityOption} className="mobile-filter-option">
                      <label className="mobile-filter-label">
                        <input
                          type="radio"
                          name="availability"
                          checked={availability === availabilityOption}
                          onChange={() => handleAvailabilityChange(availabilityOption)}
                          className="mobile-filter-radio"
                        />
                       <span className="mobile-filter-label-text">{availabilityOption}</span>
                      </label>
                    </div>
                  ))}
                  <div className="mobile-filter-option">
                    <label className="mobile-filter-label">
                      <input
                        type="radio"
                        name="availability"
                        checked={availability === null}
                        onChange={() => handleAvailabilityChange(null)}
                        className="mobile-filter-radio"
                      />
                      <span className="mobile-filter-label-text">None</span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Offcanvas>
    </div>
  );
};

FilterSidebar.propTypes = {
  onFilterChange: PropTypes.func.isRequired,
  initialFilters: PropTypes.shape({
    minPrice: PropTypes.number,
    maxPrice: PropTypes.number,
    category: PropTypes.string,
    size: PropTypes.string,
    availability: PropTypes.string,
  }),
};

FilterSidebar.defaultProps = {
  initialFilters: {
    minPrice: null,
    maxPrice: null,
    category: null,
    size: null,
    availability: null,
  },
};

export default FilterSidebar;